import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button, Spin, Empty, Tabs, Tag, Row, Col, message, Image, Table, Modal } from 'antd';
import { SearchOutlined, CheckCircleOutlined, PictureOutlined, ScissorOutlined, AuditOutlined, FileProtectOutlined, PlayCircleOutlined, ShopOutlined, ToolOutlined, QrcodeOutlined, LinkOutlined } from '@ant-design/icons';
import { traceApi } from '../../services/api';
import { scanQrCode, isUrl } from '../../utils/qrScan';
import axios from 'axios';
import EZUIKit from 'ezuikit-js';
import './style.css';

// 是否运行在微信小程序 web-view 中（小程序业务域名无法包含萤石云，视频不可播放）
const IS_MINI_PROGRAM = typeof window !== 'undefined' &&
  (window.__wxjs_environment === 'miniprogram' || /miniProgram/i.test(window.navigator.userAgent));

// 小程序内的视频占位提示
const MiniProgramVideoTip = () => (
  <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#999', fontSize: 13 }}>
    <div style={{ fontSize: 28, marginBottom: 8 }}>📹</div>
    车间监控暂不支持在小程序内播放
    <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>请在浏览器中访问 getnephrite.com 查看直播</div>
  </div>
);

const TracePage = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [video2Loading, setVideo2Loading] = useState(false);
  const [video3Loading, setVideo3Loading] = useState(false);
  const [video4Loading, setVideo4Loading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [certQrLinks, setCertQrLinks] = useState({}); // photoId -> url
  // 记录每个tab的视频是否已加载过
  const [videoLoaded, setVideoLoaded] = useState({});
  const playerRef = useRef(null);
  const player2Ref = useRef(null);
  const player3Ref = useRef(null);
  const player4Ref = useRef(null);
  const containerRef = useRef(null);
  const container2Ref = useRef(null);
  const container3Ref = useRef(null);
  const container4Ref = useRef(null);
  const initialSearchDone = useRef(false);

  // 每秒更新时钟
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 自动扫描证书照片二维码
  useEffect(() => {
    if (!result?.photos?.certificate) return;
    const certPhotos = result.photos.certificate;
    certPhotos.forEach(async (photo) => {
      if (certQrLinks[photo.id]) return;
      try {
        const scanResult = await scanQrCode(photo.id);
        if (scanResult && isUrl(scanResult)) {
          setCertQrLinks(prev => ({ ...prev, [photo.id]: scanResult }));
        }
      } catch {}
    });
  }, [result?.photos?.certificate]);

  // 从 URL 参数读取 code 和 tab，支持返回后恢复状态
  const getUrlParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return { code: params.get('code') || '', tab: params.get('tab') || '1' };
  }, []);

  const updateUrlParams = useCallback((codeVal, tabVal) => {
    const params = new URLSearchParams();
    if (codeVal) params.set('code', codeVal);
    if (tabVal && tabVal !== '1') params.set('tab', tabVal);
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }, []);

  // 页面加载时从 URL 参数自动查询
  useEffect(() => {
    if (initialSearchDone.current) return;
    initialSearchDone.current = true;
    const { code: urlCode, tab: urlTab } = getUrlParams();
    if (urlCode) {
      setCode(urlCode);
      if (urlTab) setActiveTab(urlTab);
      // 自动触发查询
      handleSearchWithCode(urlCode, urlTab);
    }
  }, []);

  // 页面加载时/切换tab时加载视频（小程序内不加载）
  useEffect(() => {
    if (IS_MINI_PROGRAM) return;
    if (!result || !result.devices?.length) return;
    if (activeTab === '1' && !videoLoaded['1']) {
      loadVideo2();
    }
    if (activeTab === '2' && !videoLoaded['2']) {
      loadVideo3();
    }
    if (activeTab === '3' && !videoLoaded['3']) {
      loadVideo();
    }
    if (activeTab === '4' && !videoLoaded['4']) {
      loadVideo4();
    }
  }, [result, activeTab]);

  // 格式化日期，只显示月日
  // 使用服务端返回的 categoryPhotoTypes，如果没有则回退到默认
  const BEAD_CATEGORIES = ['珠子', '手串', '项链'];
  const isBeadCategory = BEAD_CATEGORIES.includes(result?.batchCategory);
  const categoryPhotoTypes = result?.categoryPhotoTypes ||
    (isBeadCategory ? [1, 5, 9, 10, 11, 12, 13] : [1, 5, 9, 10, 6, 7, 8]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}年${month}月${day}日`;
  };

  const handleSearchWithCode = async (searchCode, tab) => {
    if (!searchCode.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setCertQrLinks({});
    // 重置视频加载状态
    setVideoLoaded({});
    // 清空所有播放器
    if (playerRef.current) {
      try { playerRef.current.stop(); } catch (e) {}
      playerRef.current = null;
    }
    if (player2Ref.current) {
      try { player2Ref.current.stop(); } catch (e) {}
      player2Ref.current = null;
    }
    if (player3Ref.current) {
      try { player3Ref.current.stop(); } catch (e) {}
      player3Ref.current = null;
    }
    if (player4Ref.current) {
      try { player4Ref.current.stop(); } catch (e) {}
      player4Ref.current = null;
    }

    // 更新 URL 参数
    updateUrlParams(searchCode.trim(), tab || activeTab);

    try {
      const res = await traceApi.query(searchCode.trim());
      setResult(res.data);
    } catch (err) {
      // 优先取后端返回的具体错误信息（如"查询的货品不存在"），避免显示axios英文报错
      setError(err?.response?.data?.message || err.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    handleSearchWithCode(code);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  // 加载视频（珠子车间）
  const loadVideo = async () => {
    console.log('[珠子车间] 开始加载，deviceId=2');
    setVideoLoading(true);
    try {
      const res = await axios.post('/api/trace/ys7-live-address', { deviceId: 2, companyId: result?.data?.company?.id || 1 });
      console.log('[珠子车间] API响应:', res.data);
      if (res.data.code === 200 && res.data.data) {
        const { url, accessToken } = res.data.data;

        // 等待 DOM 更新
        setTimeout(() => {
          playerRef.current = new EZUIKit.EZUIKitPlayer({
            autoplay: true,
            id: 'factory-video',
            accessToken: accessToken,
            url: url,
            template: 'standard',
            width: '100%',
            height: '300px',
          });
        }, 100);
      }
    } catch (err) {
      console.error('加载视频失败:', err);
      message.error('加载视频失败');
    } finally {
      setVideoLoading(false);
      setVideoLoaded(prev => ({ ...prev, '3': true }));
    }
  };

  // 停止视频（珠子车间）
  const stopVideo = () => {
    if (playerRef.current) {
      try {
        playerRef.current.stop();
      } catch (e) {}
      playerRef.current = null;
    }
  };

  // 加载视频2（手雕车间）
  const loadVideo2 = async () => {
    console.log('[手雕车间] 开始加载，deviceId=3');
    setVideo2Loading(true);
    try {
      const res = await axios.post('/api/trace/ys7-live-address', { deviceId: 3, companyId: result?.data?.company?.id || 1 });
      console.log('[手雕车间] API响应:', res.data);
      if (res.data.code === 200 && res.data.data) {
        const { url, accessToken } = res.data.data;

        setTimeout(() => {
          player2Ref.current = new EZUIKit.EZUIKitPlayer({
            autoplay: true,
            id: 'craft-video',
            accessToken: accessToken,
            url: url,
            template: 'standard',
            width: '100%',
            height: '250px',
          });
        }, 100);
      }
    } catch (err) {
      console.error('加载视频2失败:', err);
    } finally {
      setVideo2Loading(false);
      setVideoLoaded(prev => ({ ...prev, '1': true }));
    }
  };

  // 停止视频2
  const stopVideo2 = () => {
    if (player2Ref.current) {
      try {
        player2Ref.current.stop();
      } catch (e) {}
      player2Ref.current = null;
    }
  };

  // 加载视频3（切料车间）
  const loadVideo3 = async () => {
    console.log('[切料车间] 开始加载，deviceId=5');
    setVideo3Loading(true);
    try {
      const res = await axios.post('/api/trace/ys7-live-address', { deviceId: 5, companyId: result?.data?.company?.id || 1 });
      console.log('[切料车间] API响应:', res.data);
      if (res.data.code === 200 && res.data.data) {
        const { url, accessToken } = res.data.data;

        setTimeout(() => {
          player3Ref.current = new EZUIKit.EZUIKitPlayer({
            autoplay: true,
            id: 'cutting-video',
            accessToken: accessToken,
            url: url,
            template: 'standard',
            width: '100%',
            height: '250px',
          });
        }, 100);
      }
    } catch (err) {
      console.error('加载视频3失败:', err);
    } finally {
      setVideo3Loading(false);
      setVideoLoaded(prev => ({ ...prev, '2': true }));
    }
  };

  // 停止视频3
  const stopVideo3 = () => {
    if (player3Ref.current) {
      try {
        player3Ref.current.stop();
      } catch (e) {}
      player3Ref.current = null;
    }
  };

  // 加载视频4（工厂质检）
  const loadVideo4 = async () => {
    console.log('[视频4] 开始加载，deviceId=4');
    setVideo4Loading(true);
    try {
      const res = await axios.post('/api/trace/ys7-live-address', { deviceId: 4, companyId: result?.data?.company?.id || 1 });
      console.log('[视频4] API响应:', res.data);
      if (res.data.code === 200 && res.data.data) {
        const { url, accessToken } = res.data.data;

        setTimeout(() => {
          player4Ref.current = new EZUIKit.EZUIKitPlayer({
            autoplay: true,
            id: 'quality-video',
            accessToken: accessToken,
            url: url,
            template: 'standard',
            width: '100%',
            height: '250px',
          });
        }, 100);
      }
    } catch (err) {
      console.error('加载视频4失败:', err);
    } finally {
      setVideo4Loading(false);
      setVideoLoaded(prev => ({ ...prev, '4': true }));
    }
  };

  // 停止视频4
  const stopVideo4 = () => {
    if (player4Ref.current) {
      try {
        player4Ref.current.stop();
      } catch (e) {}
      player4Ref.current = null;
    }
  };

  // 渲染证书照片网格（自动扫描二维码，有链接显示"国检官网查询"按钮）
  const renderCertificatePhotoGrid = (photos, emptyText = '暂无照片') => {
    if (!photos || photos.length === 0) {
      return <div className="empty-photos">{emptyText}</div>;
    }

    const handleScan = async (photo) => {
      // 如果已经扫描过，直接弹窗
      if (certQrLinks[photo.id]) {
        const link = certQrLinks[photo.id];
        Modal.info({
          title: '检测到二维码链接',
          content: (
            <div>
              <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{link}</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" size="small" onClick={() => {
                  updateUrlParams(code || result?.productCode, activeTab);
                  window.location.href = link;
                }}>打开链接</Button>
                <Button size="small" onClick={() => { navigator.clipboard.writeText(link); message.success('已复制'); }}>复制链接</Button>
              </div>
            </div>
          ),
          okText: '关闭',
        });
        return;
      }
      const hide = message.loading('扫描二维码中...', 0);
      try {
        const scanResult = await scanQrCode(photo.id);
        hide();
        if (scanResult && isUrl(scanResult)) {
          setCertQrLinks(prev => ({ ...prev, [photo.id]: scanResult }));
          Modal.info({
            title: '检测到二维码链接',
            content: (
              <div>
                <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{scanResult}</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="primary" size="small" onClick={() => {
                    updateUrlParams(code || result?.productCode, activeTab);
                    window.location.href = scanResult;
                  }}>打开链接</Button>
                  <Button size="small" onClick={() => { navigator.clipboard.writeText(scanResult); message.success('已复制'); }}>复制链接</Button>
                </div>
              </div>
            ),
            okText: '关闭',
          });
        } else if (scanResult) {
          Modal.info({
            title: '检测到二维码内容',
            content: (
              <div>
                <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{scanResult}</p>
                <Button size="small" onClick={() => { navigator.clipboard.writeText(scanResult); message.success('已复制'); }}>复制内容</Button>
              </div>
            ),
            okText: '关闭',
          });
        } else {
          message.warning('该证书照片中未检测到二维码');
        }
      } catch {
        hide();
        message.error('扫描失败');
      }
    };

    // 跳转前先保存 URL 参数
    const handleOpenCertLink = (link) => {
      updateUrlParams(code || result?.productCode, activeTab);
      window.location.href = link;
    };

    // 获取第一个扫描到的证书链接
    const firstCertLink = Object.values(certQrLinks)[0] || null;

    return (
      <div className="photo-grid">
        {photos.map((photo) => (
          <div key={photo.id} className="photo-item">
            <div className="photo-img-wrapper">
              <Image
                src={photo.url}
                alt=""
                loading="lazy"
                style={{ objectFit: 'cover' }}
                width="100%"
                height="100%"
              />
              <div
                onClick={(e) => { e.stopPropagation(); handleScan(photo); }}
                style={{
                  position: 'absolute', top: 6, right: 6, zIndex: 10,
                  background: 'rgba(0,0,0,0.5)', borderRadius: 4, padding: '2px 6px',
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  color: '#fff', fontSize: 12
                }}
              >
                <QrcodeOutlined /> 扫码
              </div>
            </div>
            {photo.description && <p className="photo-desc">{photo.description}</p>}
          </div>
        ))}
      </div>
    );
  };

  // 渲染照片网格
  const renderPhotoGrid = (photos, emptyText = '暂无照片') => {
    if (!photos || photos.length === 0) {
      return <div className="empty-photos">{emptyText}</div>;
    }
    return (
      <div className="photo-grid">
        {photos.map((photo) => (
          <div key={photo.id} className="photo-item">
            <div className="photo-img-wrapper">
              <Image
                src={photo.url}
                alt=""
                loading="lazy"
                style={{ objectFit: 'cover' }}
                width="100%"
                height="100%"
              />
            </div>
            {photo.description && <p className="photo-desc">{photo.description}</p>}
          </div>
        ))}
      </div>
    );
  };

  // Tab 1: 货品信息
  const renderProductTab = () => (
    <div className="tab-content">
      <div className="info-card product-card">
        <div className="card-header">
          <CheckCircleOutlined className="icon" />
          <h2>货品信息</h2>
        </div>
        <div className="card-body">
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12}>
              <div className="info-row">
                <span className="label">编码</span>
                <span className="value code">{result.product.code}</span>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="info-row">
                <span className="label">名称</span>
                <span className="value">{result.product.name}</span>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              {result.product.category && (
                <div className="info-row">
                  <span className="label">品类</span>
                  <Tag className="jade-tag">{result.product.category}</Tag>
                </div>
              )}
            </Col>
            <Col xs={24} sm={12}>
              {result.product.material && (
                <div className="info-row">
                  <span className="label">材质</span>
                  <span className="value">{result.product.material}</span>
                </div>
              )}
            </Col>
            <Col xs={24} sm={12}>
              {result.product.weight && (
                <div className="info-row">
                  <span className="label">重量</span>
                  <span className="value">{result.product.weight}g</span>
                </div>
              )}
            </Col>
            <Col xs={24} sm={12}>
              {result.product.dimensions && (
                <div className="info-row">
                  <span className="label">尺寸</span>
                  <span className="value">{result.product.dimensions}</span>
                </div>
              )}
            </Col>
            <Col xs={24} sm={12}>
              {result.product.craftsMan && (
                <div className="info-row">
                  <span className="label">工艺师</span>
                  <span className="value">{result.product.craftsMan}</span>
                </div>
              )}
            </Col>
            <Col xs={24} sm={12}>
              {result.product.finishDate && (
                <div className="info-row">
                  <span className="label">完工日期</span>
                  <span className="value">{formatDate(result.product.finishDate)}</span>
                </div>
              )}
            </Col>
          </Row>
          {result.product.description && (
            <div className="description">
              <p>{result.product.description}</p>
            </div>
          )}
        </div>
      </div>

      {result.devices?.length > 0 && (
      <div className="info-card video-card">
        <div className="card-header">
          <h2>加工车间</h2>
          <span className="live-clock">
            {currentTime.toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </span>
        </div>
        <div className="card-body">
          <div id="craft-video" ref={container2Ref} style={{ width: '100%', height: '250px', backgroundColor: '#000' }}>
            {IS_MINI_PROGRAM ? (<MiniProgramVideoTip />) : (video2Loading && (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#fff' }}>
                <Spin tip="加载视频中..." />
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div className="info-card photos-card">
        <div className="card-header">
          <PictureOutlined className="icon" />
          <h2>成品照片</h2>
        </div>
        <div className="card-body">
          {renderPhotoGrid(result.photos.finished, '暂无成品照片')}
        </div>
      </div>

      {/* 保养记录 */}
      <div className="info-card">
        <div className="card-header">
          <ToolOutlined className="icon" />
          <h2>保养记录</h2>
        </div>
        <div className="card-body">
          {result.maintenanceRecords && result.maintenanceRecords.length > 0 ? (
            <Table
              dataSource={result.maintenanceRecords}
              rowKey={(record, index) => index}
              pagination={false}
              size="small"
              columns={[
                {
                  title: '保养日期',
                  dataIndex: 'maintenanceDate',
                  render: (date) => date ? new Date(date).toLocaleDateString('zh-CN') : '-'
                },
                {
                  title: '保养类型',
                  dataIndex: 'maintenanceType',
                  render: (type) => {
                    const types = { 1: '日常保养', 2: '深度清洁', 3: '维修', 4: '其他' };
                    return types[type] || '未知';
                  }
                },
                {
                  title: '保养门店',
                  dataIndex: 'storeName'
                },
                {
                  title: '操作人',
                  dataIndex: 'operator'
                },
                {
                  title: '描述',
                  dataIndex: 'description',
                  ellipsis: true
                }
              ]}
            />
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
              暂无保养记录
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Tab 2: 原矿溯源
  const renderOriginTab = () => (
    <div className="tab-content">
      {result.jadeType && (
        <div className="info-card batch-card">
          <div className="card-header">
            <AuditOutlined className="icon" />
            <h2>料种信息</h2>
          </div>
          <div className="card-body">
            <div className="info-section">
              <span className="label">料种名称</span>
              <span className="value">{result.jadeType.name}</span>
            </div>
            {result.jadeType.origin && (
              <div className="info-section">
                <span className="label">产地</span>
                <span className="value">{result.jadeType.origin}</span>
              </div>
            )}
            {result.jadeType.features && (
              <div className="info-section">
                <span className="label">特点</span>
                <span className="value">{result.jadeType.features}</span>
              </div>
            )}
            {result.jadeType.history && (
              <div className="info-section">
                <span className="label">历史渊源</span>
                <span className="value">{result.jadeType.history}</span>
              </div>
            )}
            {result.jadeType.culturalImage && (
              <div className="info-section">
                <span className="label">文化意象</span>
                <span className="value">{result.jadeType.culturalImage}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {result.jadeType && result.photos.jadeType && result.photos.jadeType.length > 0 && (
        <div className="info-card photos-card">
          <div className="card-header">
            <PictureOutlined className="icon" />
            <h2>矿区照片</h2>
          </div>
          <div className="card-body">
            {renderPhotoGrid(result.photos.jadeType, '暂无矿区照片')}
          </div>
        </div>
      )}

      {result.devices?.length > 0 && (
      <div className="info-card video-card">
        <div className="card-header">
          <h2>切料车间</h2>
          <span className="live-clock">
            {currentTime.toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </span>
        </div>
        <div className="card-body">
          <div id="cutting-video" ref={container3Ref} style={{ width: '100%', height: '250px', backgroundColor: '#000' }}>
            {IS_MINI_PROGRAM ? (<MiniProgramVideoTip />) : (video3Loading && (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#fff' }}>
                <Spin tip="加载视频中..." />
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div className="info-card batch-card">
        <div className="card-header">
          <AuditOutlined className="icon" />
          <h2>原石信息</h2>
        </div>
        <div className="card-body">
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12}>
              <div className="info-row">
                <span className="label">原石产地</span>
                <span className="value">{result.origin || '未知'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12}>
              {result.rawMaterialDate && (
                <div className="info-row">
                  <span className="label">开采时间</span>
                  <span className="value">{formatDate(result.rawMaterialDate)}</span>
                </div>
              )}
            </Col>
            <Col xs={24} sm={12}>
              {result.rawMaterialEntryDate && (
                <div className="info-row">
                  <span className="label">入库时间</span>
                  <span className="value">{formatDate(result.rawMaterialEntryDate)}</span>
                </div>
              )}
            </Col>
          </Row>
        </div>
      </div>

      <div className="info-card photos-card">
        <div className="card-header">
          <PictureOutlined className="icon" />
          <h2>原石照片</h2>
        </div>
        <div className="card-body">
          {renderPhotoGrid(result.photos.rawMaterial, '暂无原石照片')}
        </div>
      </div>

      {result.photos.rawMaterialEntry?.length > 0 && (
        <div className="info-card photos-card">
          <div className="card-header">
            <PictureOutlined className="icon" />
            <h2>原石入库照片</h2>
          </div>
          <div className="card-body">
            {renderPhotoGrid(result.photos.rawMaterialEntry, '暂无原石入库照片')}
          </div>
        </div>
      )}
    </div>
  );

  // Tab 3: 生产加工
  const renderProductionTab = () => {
    // 照片类型ID到photos key的映射
    const photoKeyMap = {
      1: 'rawMaterial', 2: 'production', 5: 'cutting', 6: 'planning',
      7: 'handwork', 8: 'polishing', 9: 'rawMaterialEntry', 10: 'cuttingBlock',
      11: 'pillarDrilling', 12: 'beadGrinding', 13: 'drilling',
    };
    const photoTitleMap = {
      1: '原石照片', 2: '生产过程照片', 5: '切料照片', 6: '规划照片',
      7: '手修照片', 8: '抛光照片', 9: '原石入库照片', 10: '切料标块照片',
      11: '打柱照片', 12: '磨珠照片', 13: '穿珠子照片',
    };

    // 根据 categoryPhotoTypes 动态构建批次照片卡片列表
    const batchPhotoCards = [];
    categoryPhotoTypes.forEach(typeId => {
      const key = photoKeyMap[typeId];
      const photos = key ? (result.photos[key] || []) : [];
      if (photos.length > 0) {
        batchPhotoCards.push({
          key: key || `type_${typeId}`,
          title: photoTitleMap[typeId] || `照片类型${typeId}`,
          photos,
          icon: <PictureOutlined className="icon" />,
        });
      }
    });

    // 动态构建其他货品级照片卡片（除成品type3和证书type4外）
    const productPhotoTypeList = result?.productPhotoTypes || [];
    const otherProductCards = [];
    productPhotoTypeList.forEach(pt => {
      // 成品照片(type3)和检测证书(type4)有自己的tab，不在这里显示
      if (pt.id === 3 || pt.id === 4) return;
      const key = String(pt.id);
      const photos = result.photos?.otherProduct?.[key] || [];
      // 半成品照片(type14)有title字段
      if (pt.id === 14) {
        const semiPhotos = result.photos?.semiFinished || [];
        if (semiPhotos.length > 0) {
          otherProductCards.push({
            key: 'semiFinished',
            title: semiPhotos[0]?.title || pt.name,
            photos: semiPhotos,
            icon: <PictureOutlined className="icon" />,
          });
        }
      } else if (photos.length > 0) {
        otherProductCards.push({
          key: `product_${key}`,
          title: pt.name,
          photos,
          icon: <PictureOutlined className="icon" />,
        });
      }
    });

    return (
      <div className="tab-content">
        {result.devices?.length > 0 && (
        <div className="info-card video-card">
          <div className="card-header">
            <h2>生产车间</h2>
            <span className="live-clock">
              {currentTime.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour12: false,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })}
            </span>
          </div>
          <div className="card-body">
            <div id="factory-video" ref={containerRef} style={{ width: '100%', height: '300px', backgroundColor: '#000' }}>
              {IS_MINI_PROGRAM ? (<MiniProgramVideoTip />) : (videoLoading && (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#fff' }}>
                  <Spin tip="加载视频中..." />
                </div>
              ))}
            </div>
          </div>
        </div>
        )}

        {result.productionStartDate && (
          <div className="info-card batch-card">
            <div className="card-header">
              <h2>生产时间</h2>
            </div>
            <div className="card-body">
              <Row gutter={[16, 12]}>
                <Col xs={24} sm={12}>
                  <div className="info-row">
                    <span className="label">生产开始</span>
                    <span className="value">{formatDate(result.productionStartDate)}</span>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  {result.productionEndDate && (
                    <div className="info-row">
                      <span className="label">生产完成</span>
                      <span className="value">{formatDate(result.productionEndDate)}</span>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          </div>
        )}

        {batchPhotoCards.map(({ key, title, icon, photos }) => (
          <div key={key} className="info-card photos-card">
            <div className="card-header">
              {icon}
              <h2>{title}</h2>
            </div>
            <div className="card-body">
              {renderPhotoGrid(photos, `暂无${title}`)}
            </div>
          </div>
        ))}

        {/* 其他货品级照片（半成品等），显示在生产加工最后 */}
        {otherProductCards.map(({ key, title, icon, photos }) => (
          <div key={key} className="info-card photos-card">
            <div className="card-header">
              {icon}
              <h2>{title}</h2>
            </div>
            <div className="card-body">
              {renderPhotoGrid(photos, `暂无${title}`)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Tab 4: 检测证书
  const renderCertificateTab = () => (
    <div className="tab-content">
      {result.devices?.length > 0 && (
      <div className="info-card video-card">
        <div className="card-header">
          <h2>工厂质检</h2>
          <span className="live-clock">
            {currentTime.toLocaleString('zh-CN', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </span>
        </div>
        <div className="card-body">
          <div id="quality-video" ref={container4Ref} style={{ width: '100%', height: '250px', backgroundColor: '#000' }}>
            {IS_MINI_PROGRAM ? (<MiniProgramVideoTip />) : (video4Loading && (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000', color: '#fff' }}>
                <Spin tip="加载视频中..." />
              </div>
            ))}
          </div>
        </div>
      </div>
      )}

      <div className="info-card photos-card">
        <div className="card-header">
          <FileProtectOutlined className="icon" />
          <h2>检测证书</h2>
        </div>
        <div className="card-body">
          {Object.keys(certQrLinks).length > 0 && (
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <Button
                type="primary"
                size="large"
                icon={<LinkOutlined />}
                onClick={() => {
                  const link = Object.values(certQrLinks)[0];
                  Modal.info({
                    title: '国检官网查询',
                    content: (
                      <div>
                        <p style={{ wordBreak: 'break-all', marginBottom: 12 }}>{link}</p>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                          <Button type="primary" size="small" onClick={() => window.open(link, '_blank')}>打开链接</Button>
                          <Button size="small" onClick={() => { navigator.clipboard.writeText(link); message.success('已复制链接'); }}>复制链接</Button>
                        </div>
                      </div>
                    ),
                    okText: '关闭',
                  });
                }}
                style={{ background: '#8B7355', borderColor: '#8B7355', height: 44, fontSize: 16, paddingInline: 32 }}
              >
                国检官网查询
              </Button>
            </div>
          )}
          {renderCertificatePhotoGrid(result.photos.certificate, '暂无检测证书')}
        </div>
      </div>
    </div>
  );

  const tabItems = [
    {
      key: '1',
      label: '货品信息',
      children: result && renderProductTab()
    },
    {
      key: '2',
      label: '原矿溯源',
      children: result && renderOriginTab()
    },
    {
      key: '3',
      label: '生产加工',
      children: result && renderProductionTab()
    },
    {
      key: '4',
      label: '检测证书',
      children: result && renderCertificateTab()
    }
  ];

  return (
    <div className="trace-page">
      <header className="trace-header">
        <div className="logo">
          <div className="logo-icon">玉</div>
          <h1>新疆和田玉溯源</h1>
        </div>
        <p className="subtitle">让买和田玉变得简单</p>
      </header>

      <section className="search-section">
        <div className="search-box">
          <Input
            size="large"
            placeholder="请输入货品编码"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyPress={handleKeyPress}
            prefix={<SearchOutlined />}
            className="search-input"
          />
          <Button
            type="primary"
            size="large"
            onClick={handleSearch}
            loading={loading}
            className="search-btn"
          >
            查询
          </Button>
        </div>
      </section>

      <section className="result-section">
        {loading && (
          <div className="loading">
            <Spin size="large" />
            <p>正在查询溯源信息...</p>
          </div>
        )}

        {error && !loading && (
          <div className="error-box">
            <Empty
              description={error}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}

        {result && !loading && (
          <div className="result-content">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                if (result) updateUrlParams(code || result.productCode, key);
              }}
              items={tabItems}
              size="large"
            />

            <div className="result-footer">
              <p>查询时间: {formatDate(result.queryTime)}</p>
              <p className="brand">新疆和田玉溯源系统</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default TracePage;
